import { MoreHorizontal, type LucideIcon } from 'lucide-react';
import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { buttonVariants } from '@/components/ui/button-variants';
import { cn } from '@/lib/utils';

export interface ActionMenuItem {
  label: string;
  icon?: LucideIcon | undefined;
  /** A second, quieter line: what the action involves, where that is not obvious from its name. */
  hint?: string | undefined;
  title?: string | undefined;
  disabled?: boolean | undefined;
  tone?: 'danger' | undefined;
  /** Draws a divider above this item, to set destructive actions apart from the rest. */
  separated?: boolean | undefined;
  onSelect: () => void;
}

/**
 * The "⋯" menu that holds a row's secondary actions.
 *
 * ## Why rows have one at all
 *
 * A backup row used to carry five buttons of equal weight — two downloads, test, restore and a bin —
 * so the one that destroys nothing and the one that overwrites production looked the same, and the
 * row wrapped onto three lines on a phone. The actions a customer reaches for stay on the row; the
 * rest move here, with the destructive one last and set apart.
 *
 * ## Keyboard and screen readers
 *
 * A real menu: the trigger announces that it opens one, focus moves into it on open and returns to
 * the trigger on Escape, arrow keys move between items and skip disabled ones, and Tab or a click
 * outside closes it. It opens upwards when there is no room below, so the last row of a long list
 * does not open a menu off the bottom of the screen.
 */
export function ActionMenu({ label, items }: { label: string; items: ActionMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const [above, setAbove] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    // Escape is handled here as well as on the menu, because a menu whose every item is disabled
    // never receives focus, and would otherwise have no keyboard way to close.
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !menu.current || !trigger.current) return;

    const box = trigger.current.getBoundingClientRect();
    const height = menu.current.offsetHeight;
    setAbove(window.innerHeight - box.bottom < height + 16 && box.top > height + 16);

    enabledItemsIn(menu.current)[0]?.focus();
  }, [open]);

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const enabled = enabledItemsIn(menu.current);
    const index = enabled.findIndex((item) => item === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        enabled[(index + 1) % enabled.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        enabled[(index - 1 + enabled.length) % enabled.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        enabled[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        enabled[enabled.length - 1]?.focus();
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  }

  return (
    <div ref={root} className="relative">
      <button
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label}
        title="More actions"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'sm' }),
          'w-8 px-0 text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground',
          open && 'bg-foreground/[0.06] text-foreground',
        )}
      >
        <MoreHorizontal />
      </button>

      {open && (
        <div
          ref={menu}
          id={id}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className={cn(
            'absolute right-0 z-30 w-60 rounded-lg bg-card p-1 ring-1 ring-black/[0.08] dark:ring-white/[0.08]',
            'shadow-[0_12px_32px_-8px_rgb(15_23_42/0.25)] dark:shadow-[0_16px_40px_-8px_rgb(0_0_0/0.7)]',
            'animate-in fade-in-0 zoom-in-95',
            above ? 'bottom-full mb-1.5 origin-bottom-right' : 'top-full mt-1.5 origin-top-right',
          )}
        >
          {items.map((item) => {
            const Icon = item.icon;

            return (
              <Fragment key={item.label}>
                {item.separated && <div role="separator" className="my-1 h-px bg-foreground/[0.07]" />}
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  title={item.title}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                  className={cn(
                    'group flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                    'hover:bg-foreground/[0.06] focus:bg-foreground/[0.06] focus:outline-none',
                    'disabled:cursor-not-allowed disabled:hover:bg-transparent',
                    item.tone === 'danger' ? 'text-danger' : 'text-foreground',
                  )}
                >
                  {Icon && <Icon className="mt-0.5 size-4 shrink-0 opacity-80 group-disabled:opacity-30" aria-hidden />}
                  <span className="min-w-0">
                    <span className="block leading-5 group-disabled:opacity-40">{item.label}</span>
                    {item.hint !== undefined && (
                      <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">{item.hint}</span>
                    )}
                  </span>
                </button>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

function enabledItemsIn(menu: HTMLElement | null): HTMLButtonElement[] {
  return Array.from(menu?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);
}
